import { createCalendar, getWeeksInMonth, isSameDay, type CalendarDate } from "@internationalized/date";
import type { RangeCalendarState } from "@react-stately/calendar";
import type { DateSegment as DateSegmentType, DateFieldState } from "@react-stately/datepicker";
import type { RangeValue } from "@react-types/shared";
import { type FC, useEffect, useRef } from "react";
import {
    DismissButton,
    useDateRangePicker,
    useDateField,
    useDateSegment,
    useInteractOutside,
    useLocale,
    useRangeCalendar,
    useCalendarGrid,
    useCalendarCell,
    useButton,
} from "react-aria";
import type { AriaButtonProps } from "react-aria";
import type { DateRangePickerState } from "react-stately";
import { useDateRangePickerState, useDateFieldState, useRangeCalendarState } from "react-stately";

import { IconCalendar } from "@/assets/icons/geist/IconCalendar";
import { cn } from "@/components/podkit/lib/cn";

export type DateRangePreset = {
    label: string;
    /** Calendar-aware duration to subtract from the end date. */
    subtract: { days?: number; weeks?: number; months?: number; years?: number };
};

type DateRangePickerFieldProps = {
    value: { start: CalendarDate; end: CalendarDate };
    onChange: (value: { start: CalendarDate; end: CalendarDate }) => void;
    minValue?: CalendarDate;
    maxValue?: CalendarDate;
    /** Exposes react-aria's validation state so the parent can disable actions. */
    onValidationChange?: (isInvalid: boolean) => void;
    /** When true, forces the invalid (red border) visual state from the parent. */
    isInvalid?: boolean;
    /** When provided, shows a preset sidebar alongside the calendar. */
    presets?: DateRangePreset[];
};

export const DateRangePickerField: FC<DateRangePickerFieldProps> = ({
    value,
    onChange,
    minValue,
    maxValue,
    onValidationChange,
    isInvalid: isInvalidOverride,
    presets,
}) => {
    const ref = useRef<HTMLDivElement>(null);
    const startFieldRef = useRef<HTMLDivElement>(null);
    const endFieldRef = useRef<HTMLDivElement>(null);

    const handleChange = (newValue: RangeValue<CalendarDate> | null) => {
        if (newValue) {
            onChange(newValue);
        }
    };

    const state = useDateRangePickerState({
        value,
        onChange: handleChange,
        minValue,
        maxValue,
        granularity: "day",
    });

    const { groupProps, startFieldProps, endFieldProps, buttonProps, dialogProps, calendarProps } = useDateRangePicker(
        { value, onChange: handleChange, minValue, maxValue, granularity: "day", "aria-label": "Custom date range" },
        state,
        ref,
    );

    const isInvalid = state.isInvalid || !!isInvalidOverride;

    useEffect(() => {
        onValidationChange?.(state.isInvalid);
    }, [state.isInvalid, onValidationChange]);

    return (
        <div className="relative" data-testid="date-range-picker">
            <div
                {...groupProps}
                ref={ref}
                className={cn(
                    "inline-flex items-center gap-1.5 rounded-lg border bg-surface-pure px-3 py-1.5",
                    isInvalid ? "border-red-500" : "border-border-base",
                )}
            >
                <DateField {...startFieldProps} fieldRef={startFieldRef} />
                <span className="text-sm text-content-tertiary" aria-hidden="true">
                    &rarr;
                </span>
                <DateField {...endFieldProps} fieldRef={endFieldRef} />
                <CalendarButton {...buttonProps} onPress={() => state.setOpen(!state.isOpen)} />
            </div>
            {state.isOpen && (
                <CalendarPopover
                    state={state}
                    triggerRef={ref}
                    dialogProps={dialogProps as React.HTMLAttributes<HTMLDivElement>}
                    calendarProps={calendarProps as unknown as Parameters<typeof useRangeCalendarState>[0]}
                    presets={presets}
                    value={value}
                    minValue={minValue}
                    maxValue={maxValue}
                    onChange={onChange}
                />
            )}
        </div>
    );
};

const CalendarButton: FC<AriaButtonProps> = (props) => {
    const ref = useRef<HTMLButtonElement>(null);
    const { buttonProps } = useButton(props, ref);
    return (
        <button
            {...buttonProps}
            ref={ref}
            className="focus-visible:ring-ring ml-1 rounded p-0.5 text-content-secondary hover:bg-surface-secondary hover:text-content-primary focus:outline-none focus-visible:ring-2"
        >
            <IconCalendar size="sm" className="h-4 w-4" />
        </button>
    );
};

type CalendarPopoverProps = {
    state: DateRangePickerState;
    triggerRef: React.RefObject<HTMLDivElement | null>;
    dialogProps: React.HTMLAttributes<HTMLDivElement>;
    calendarProps: Parameters<typeof useRangeCalendarState>[0];
    presets?: DateRangePreset[];
    value: { start: CalendarDate; end: CalendarDate };
    minValue?: CalendarDate;
    maxValue?: CalendarDate;
    onChange: (value: { start: CalendarDate; end: CalendarDate }) => void;
};

const CalendarPopover: FC<CalendarPopoverProps> = ({
    state,
    triggerRef,
    dialogProps,
    calendarProps,
    presets,
    value,
    minValue,
    maxValue,
    onChange,
}) => {
    const popoverRef = useRef<HTMLDivElement>(null);

    useInteractOutside({
        ref: popoverRef,
        isDisabled: !state.isOpen,
        onInteractOutside: (e) => {
            if (triggerRef.current?.contains(e.target as Node)) return;
            state.setOpen(false);
        },
    });

    const handlePresetClick = (preset: DateRangePreset) => {
        const end = maxValue ?? value.end;
        let start = end.subtract(preset.subtract);
        if (minValue && start.compare(minValue) < 0) {
            start = minValue;
        }
        onChange({ start, end });
        state.setOpen(false);
    };

    return (
        <div
            {...dialogProps}
            ref={popoverRef}
            className="absolute left-0 top-full z-50 mt-1 flex rounded-lg border border-border-base bg-surface-pure shadow-lg"
        >
            <DismissButton onDismiss={() => state.setOpen(false)} />
            {presets && presets.length > 0 && (
                <div
                    className="flex flex-col gap-0.5 border-r border-border-base p-2"
                    role="listbox"
                    aria-label="Date range presets"
                >
                    {presets.map((preset) => {
                        const end = maxValue ?? value.end;
                        const expectedStart = end.subtract(preset.subtract);
                        const isActive = isSameDay(value.end, end) && isSameDay(value.start, expectedStart);
                        return (
                            <button
                                key={preset.label}
                                role="option"
                                aria-selected={isActive}
                                data-tracking-id="date-range-preset"
                                onClick={() => handlePresetClick(preset)}
                                className={cn(
                                    "whitespace-nowrap rounded-md px-3 py-1.5 text-left text-sm transition-colors",
                                    isActive
                                        ? "bg-surface-button-primary-accent text-content-invert"
                                        : "text-content-secondary hover:bg-surface-secondary hover:text-content-primary",
                                )}
                            >
                                {preset.label}
                            </button>
                        );
                    })}
                </div>
            )}
            <div className="p-3">
                <RangeCalendar {...calendarProps} />
            </div>
            <DismissButton onDismiss={() => state.setOpen(false)} />
        </div>
    );
};

type DateFieldProps = Record<string, unknown> & {
    fieldRef: React.RefObject<HTMLDivElement | null>;
};

const DateField: FC<DateFieldProps> = ({ fieldRef, ...props }) => {
    const { locale } = useLocale();
    const state = useDateFieldState({
        ...(props as unknown as Parameters<typeof useDateFieldState>[0]),
        locale,
        createCalendar,
    });

    const { fieldProps } = useDateField(
        props as unknown as Parameters<typeof useDateField>[0],
        state,
        fieldRef as unknown as React.RefObject<HTMLDivElement>,
    );

    return (
        <div {...fieldProps} ref={fieldRef as React.Ref<HTMLDivElement>} className="inline-flex items-center">
            {state.segments.map((segment, i) => (
                <Segment key={i} segment={segment} state={state} />
            ))}
        </div>
    );
};

type SegmentProps = {
    segment: DateSegmentType;
    state: DateFieldState;
};

const Segment: FC<SegmentProps> = ({ segment, state }) => {
    const ref = useRef<HTMLDivElement>(null);
    const { segmentProps } = useDateSegment(segment, state, ref as unknown as React.RefObject<HTMLDivElement>);

    return (
        <div
            {...segmentProps}
            ref={ref}
            className={cn(
                "rounded px-0.5 text-sm tabular-nums outline-none",
                segment.type === "literal" ? "text-content-tertiary" : "text-content-primary",
                segment.isEditable && "focus:bg-surface-button-primary-accent focus:text-content-invert",
            )}
        >
            {segment.text}
        </div>
    );
};

const RangeCalendar: FC<Parameters<typeof useRangeCalendarState>[0]> = (props) => {
    const ref = useRef<HTMLDivElement>(null);
    const { locale } = useLocale();
    const state = useRangeCalendarState({
        ...(props as unknown as Parameters<typeof useRangeCalendarState>[0]),
        locale,
        createCalendar,
    });

    const { calendarProps, prevButtonProps, nextButtonProps, title } = useRangeCalendar(
        props as unknown as Parameters<typeof useRangeCalendar>[0],
        state,
        ref,
    );

    return (
        <div {...calendarProps} ref={ref} className="w-[280px]">
            <div className="mb-2 flex items-center justify-between">
                <CalendarNavButton {...prevButtonProps}>&lsaquo;</CalendarNavButton>
                <span className="text-sm font-medium text-content-primary">{title}</span>
                <CalendarNavButton {...nextButtonProps}>&rsaquo;</CalendarNavButton>
            </div>
            <CalendarGrid state={state} />
        </div>
    );
};

const CalendarNavButton: FC<AriaButtonProps & { children: React.ReactNode }> = (props) => {
    const ref = useRef<HTMLButtonElement>(null);
    const { buttonProps } = useButton(props, ref);
    return (
        <button
            {...buttonProps}
            ref={ref}
            className="focus-visible:ring-ring flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-secondary hover:text-content-primary focus:outline-none focus-visible:ring-2 disabled:opacity-30"
        >
            {props.children}
        </button>
    );
};

type CalendarGridProps = {
    state: RangeCalendarState;
};

const CalendarGrid: FC<CalendarGridProps> = ({ state }) => {
    const { locale } = useLocale();
    const { gridProps, headerProps, weekDays } = useCalendarGrid({}, state);

    const weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale);

    return (
        <table {...gridProps} className="w-full border-separate" style={{ borderSpacing: "0 2px" }}>
            <thead {...headerProps}>
                <tr>
                    {weekDays.map((day, i) => (
                        <th key={i} className="pb-1 text-center text-xs font-normal text-content-tertiary">
                            {day}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {Array.from({ length: weeksInMonth }, (_, weekIndex) => (
                    <tr key={weekIndex}>
                        {state
                            .getDatesInWeek(weekIndex)
                            .map((date, i) =>
                                date ? <CalendarCell key={i} state={state} date={date} /> : <td key={i} />,
                            )}
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

type CalendarCellProps = {
    state: RangeCalendarState;
    date: CalendarDate;
};

const CalendarCell: FC<CalendarCellProps> = ({ state, date }) => {
    const ref = useRef<HTMLDivElement>(null);
    const { cellProps, buttonProps, isSelected, isOutsideVisibleRange, isDisabled, formattedDate, isInvalid } =
        useCalendarCell({ date }, state, ref);

    const isRangeStart = state.highlightedRange ? isSameDay(date, state.highlightedRange.start) : false;
    const isRangeEnd = state.highlightedRange ? isSameDay(date, state.highlightedRange.end) : false;
    const isInRange = isSelected && !isRangeStart && !isRangeEnd;

    const isSingleDay = isRangeStart && isRangeEnd;

    // Range background: a before pseudo-element covers the correct portion of
    // the cell. For start cells it covers the right half, for end cells the
    // left half, and for in-range cells the full width. This keeps the bar
    // connected while not spilling past the circular endpoints.
    const rangeBgClass = cn(
        isSingleDay && "",
        !isSingleDay &&
            isRangeStart &&
            "before:absolute before:inset-y-0 before:left-1/2 before:right-0 before:bg-surface-button-primary-accent/15",
        !isSingleDay &&
            isRangeEnd &&
            "before:absolute before:inset-y-0 before:left-0 before:right-1/2 before:bg-surface-button-primary-accent/15",
        isInRange && "before:absolute before:inset-0 before:bg-surface-button-primary-accent/15",
    );

    return (
        <td {...cellProps} className={cn("relative p-0 text-center", rangeBgClass)}>
            <div
                {...buttonProps}
                ref={ref}
                hidden={isOutsideVisibleRange}
                className={cn(
                    "focus-visible:ring-ring relative z-10 mx-auto flex h-8 w-8 cursor-pointer items-center justify-center text-sm outline-none focus-visible:ring-2",
                    isDisabled && "cursor-default text-content-tertiary opacity-40",
                    !isSelected && !isDisabled && "rounded text-content-primary hover:bg-surface-secondary",
                    (isRangeStart || isRangeEnd) && "rounded-full bg-surface-button-primary-accent text-content-invert",
                    isInRange && "text-content-primary",
                    isInvalid && "text-content-destructive",
                )}
            >
                {formattedDate}
            </div>
        </td>
    );
};
